import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { CardSliceContent } from '@app/code/src/types/prismic.types'
import { FreeQrCodeGeneratorVariation1Document } from '@dtx-company/prismic-codegen/src/generated.types'
import { HomepageBelowFoldExperimentData } from '@app/code/src/components/Homepage/HomepageStaticContent/variants/types'
import { HowToSchema } from '@app/code/src/components/Homepage/HowToSchema'
import { LOCGPrismicProvider } from '@app/code/src/components/LoggedOutCodeGeneratorV2/LOCGPrismicContext'
import { LoggedOutCodeGenerator } from '@app/code/src/components/LoggedOutCodeGeneratorV2/LoggedOutCodeGenerator'
import { Meta } from '../../../constants/meta'
import { NonGeneratorAboveFoldProps } from '@app/code/src/components/Homepage/NonGeneratorAboveFold/NonGeneratorAboveFold.types'
import { PageContainer } from '../../layout'
import { ProUpsellBanner } from '@app/code/src/components/Homepage/ProUpsellBanner'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useEffect } from 'react'
import { useLoggedOutGeneratorV2Experiment } from '@app/code/src/components/LoggedOutCodeGeneratorV2/hooks/useLoggedOutGeneratorV2Experiment'
import dynamic from 'next/dynamic'

const NonGeneratorAboveFold = dynamic<NonGeneratorAboveFoldProps>(() =>
  import('@app/code/src/components/Homepage/NonGeneratorAboveFold/NonGeneratorAboveFold').then(
    mod => mod.NonGeneratorAboveFold
  )
)

const LongAndColorful = dynamic<{ data: HomepageBelowFoldExperimentData['v2'] }>(
  () =>
    import('@app/code/src/components/Homepage/HomepageStaticContent/variants/V2-LongAndColorful')
)

export interface FreeQrCodeGeneratorPageProps {
  bannerData: { [key: string]: CardSliceContent }
  locgV2Data: FreeQrCodeGeneratorVariation1Document['data']
  homepageBelowFoldExperimentData: HomepageBelowFoldExperimentData
  nonGeneratorAboveFoldData: NonGeneratorAboveFoldProps
  injectedMeta?: Meta
  swrFallback?: Record<string, unknown>
  isMobile?: boolean
}

export const FreeQrCodeGeneratorPage = (props: FreeQrCodeGeneratorPageProps): JSX.Element => {
  const { bannerData, homepageBelowFoldExperimentData, injectedMeta } = props
  const { hideFooter } = useCodeSelector((state: RootCodeState) => {
    return state.settings
  })
  const showStaticContent = useCodeSelector(state => state.homePage.showStaticContent)

  const LOCGVariant = useLoggedOutGeneratorV2Experiment()
  const displayLOCGV2 = LOCGVariant === 'new_locg_variant'
  const hideGenerator = LOCGVariant === 'removal_variant'

  /** Track what variation of LOGGED_OUT_CODE_GENERATOR_V2_TEST the user is viewing */
  useEffect(() => {
    if (hideGenerator) {
      fireAnalyticsEvent('locgtest_viewed_locgremoved')
    }
  }, [displayLOCGV2, hideGenerator])

  return (
    <PageContainer
      disableSideNav
      injectedMeta={injectedMeta}
      page={Routes.FREE_QR_CODE_GENERATOR}
      disableFooter={hideFooter}
      disableNotifications
    >
      <LOCGPrismicProvider
        bannerData={props.bannerData}
        locgV2Data={props.locgV2Data}
        homepageBelowFoldExperimentData={props.homepageBelowFoldExperimentData}
        nonGeneratorAboveFoldData={props.nonGeneratorAboveFoldData}
      >
        <Box display="block">
          {hideGenerator ? (
            <NonGeneratorAboveFold {...props.nonGeneratorAboveFoldData} />
          ) : (
            <LoggedOutCodeGenerator />
          )}

          <Box display={['none', 'none', 'none', 'block']}>
            <ProUpsellBanner
              data={bannerData.proUpsellBannerCard}
              sectionId={'pro-upsell-banner'}
            />
          </Box>

          {showStaticContent && <LongAndColorful data={homepageBelowFoldExperimentData.v2} />}
        </Box>
      </LOCGPrismicProvider>
      <HowToSchema />
    </PageContainer>
  )
}
